import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
// Redux
import { useSelector } from "react-redux";
// Constants
import { WEB_SERVICE_DASHBOARD_ROUTE, budgetGroups, licenseConfig, taxConfig } from "../../config/constants";
import { getLicenseTotal, getFinalTotal, formatCurrency } from "../../config/budget";
import { formatDate } from "../../config/dates";
import i18n, { languageCodeToLocale, getLanguageCode, defaultLanguage } from "../../i18n";
// Components
import { EmptyMessage } from "./styles";
import Loader from "../loader";
import DistributorLayout from "../distributorLayout";
import ArticleGroup from "./sections/articleGroup";
import LicenseGroup from "./sections/licenseGroup";
import TotalsBox from "./sections/totalsBox";
import QuoteDetails from "./sections/quoteDetails";
import UseFetch from "../../config/useFetch";
// Assets
import itemImages from "../../assets/items";

const BudgetGenerator = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [currency, setCurrency] = useState("");
  const [prices, setPrices] = useState({});
  const [quantities, setQuantities] = useState({});
  const [therapists, setTherapists] = useState(0);
  const [years, setYears] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [tax, setTax] = useState(taxConfig.defaultValue);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  const isLoggedIn = useSelector(({ user }) => user.isLoggedIn);
  const distributorDetails = useSelector(({ distributor }) => distributor.details);
  const { t } = useTranslation();

  // Fetching the distributor articles and prices
  useEffect(() => {
    const getArticlesSignal = new AbortController();

    if (isLoggedIn) {
      UseFetch("get", `${WEB_SERVICE_DASHBOARD_ROUTE}/budget/get-articles`, getArticlesSignal)
        .then(({ data }) => {
          setCurrency(data.distributor.currency);
          setPrices(data.articles.reduce((acc, { key, price }) => ({ ...acc, [key]: price }), {}));
          // El idioma por defecto lo marca el distribuidor; el selector puede cambiarlo luego.
          i18n.changeLanguage(getLanguageCode(data.distributor.language));
          setIsLoading(false);
        })
        .catch((error) => {
          console.log("Get budget articles: ", error);
          toast.error(i18n.t("budget.loadError"));
          setIsLoading(false);
        });
    }

    return () => {
      getArticlesSignal.abort();
    };
  }, [isLoggedIn]);

  const locale = languageCodeToLocale[i18n.language] || languageCodeToLocale[defaultLanguage];
  const format = (amount) => formatCurrency(amount, currency, locale);
  const getQuantity = (articleKey) => quantities[articleKey] || 0;
  const setQuantity = (articleKey, value) => setQuantities((prev) => ({ ...prev, [articleKey]: value }));

  const licenseTotal = useMemo(() => {
    const yearlyFee = prices.rgs_ecosystem_license;
    if (!yearlyFee) return 0;

    return getLicenseTotal(yearlyFee, therapists, years);
  }, [prices, therapists, years]);

  const subtotal = useMemo(() => {
    const hardwareTotal = Object.entries(prices).reduce((total, [articleKey, price]) => {
      if (articleKey === "rgs_ecosystem_license") return total;

      return total + price * (quantities[articleKey] || 0);
    }, 0);

    return hardwareTotal + licenseTotal;
  }, [prices, quantities, licenseTotal]);

  const renderPack = ({ key, articleKey, contents }) => {
    const price = prices[articleKey];
    if (!price) return null;

    const quantity = getQuantity(articleKey);
    const rows = contents.map((contentKey) => ({
      key: contentKey,
      name: t(`budget.articles.${articleKey}.contents.${contentKey}.name`),
      description: t(`budget.articles.${articleKey}.contents.${contentKey}.description`),
      image: itemImages[`${articleKey}_${contentKey}`],
    }));

    return (
      <ArticleGroup
        key={key}
        title={t(`budget.articles.${articleKey}.name`)}
        columnLabels={t("budget.columns", { returnObjects: true })}
        header={{
          price: format(price),
          quantity: { value: quantity, onChange: (value) => setQuantity(articleKey, value), max: Infinity },
          total: format(price * quantity),
        }}
        rows={rows}
      />
    );
  };

  const renderArticles = ({ key, articleKeys }) => {
    const availableKeys = articleKeys.filter((articleKey) => prices[articleKey]);
    if (!availableKeys.length) return null;

    const groupTotal = availableKeys.reduce((total, articleKey) => total + prices[articleKey] * getQuantity(articleKey), 0);
    const rows = availableKeys.map((articleKey) => ({
      key: articleKey,
      name: t(`budget.articles.${articleKey}.name`),
      description: t(`budget.articles.${articleKey}.description`),
      image: itemImages[articleKey],
      price: format(prices[articleKey]),
      quantity: {
        value: getQuantity(articleKey),
        onChange: (value) => setQuantity(articleKey, value),
        max: Infinity,
      },
      total: format(prices[articleKey] * getQuantity(articleKey)),
    }));

    return (
      <ArticleGroup
        key={key}
        title={t(`budget.groups.${key}`)}
        columnLabels={t("budget.columns", { returnObjects: true })}
        header={{ price: "", quantity: null, total: format(groupTotal) }}
        rows={rows}
      />
    );
  };

  const renderLicense = ({ key, articleKey }) => {
    if (!prices[articleKey]) return null;

    return (
      <LicenseGroup
        key={key}
        title={t(`budget.groups.${key}`)}
        columnLabels={t("budget.columns", { returnObjects: true })}
        name={t(`budget.articles.${articleKey}.name`)}
        features={t(`budget.articles.${articleKey}.features`, { returnObjects: true })}
        therapists={{ value: therapists, onChange: setTherapists, max: licenseConfig.maxTherapists }}
        years={{ value: years, onChange: setYears, max: licenseConfig.maxYears }}
        total={format(licenseTotal)}
      />
    );
  };

  const renderGroup = (group) => {
    switch (group.type) {
      case "pack":
        return renderPack(group);
      case "articles":
        return renderArticles(group);
      case "license":
        return renderLicense(group);
      default:
        return null;
    }
  };

  // Pedir los datos del presupuesto antes de avisar de que esta vacio obligaria
  // a rellenar seis campos para nada, asi que se comprueba al abrir.
  const openQuoteModal = () => {
    if (!subtotal) return toast.warn(t("quote.emptyBudget"));

    return setIsQuoteModalOpen(true);
  };

  // Solo entra en el PDF lo que tenga cantidad: una linea a cero en un
  // presupuesto que ve el cliente solo despista.
  const buildPdfGroups = () => {
    const hardwareLabels = {
      description: t("pdf.description"),
      first: t("pdf.price"),
      second: t("pdf.quantity"),
      total: t("pdf.total"),
    };
    const licenseLabels = {
      description: t("pdf.description"),
      first: t("pdf.therapists"),
      second: t("pdf.years"),
      total: t("pdf.total"),
    };

    return budgetGroups
      .map((group) => {
        if (group.type === "pack") {
          const price = prices[group.articleKey];
          const quantity = getQuantity(group.articleKey);
          if (!price || !quantity) return null;

          return {
            title: t(`budget.articles.${group.articleKey}.name`),
            columnLabels: hardwareLabels,
            price: format(price),
            quantity,
            total: format(price * quantity),
            items: group.contents.map((contentKey) => ({
              name: t(`budget.articles.${group.articleKey}.contents.${contentKey}.name`),
              bullets: [t(`budget.articles.${group.articleKey}.contents.${contentKey}.description`)],
              imageSrc: itemImages[`${group.articleKey}_${contentKey}`],
            })),
          };
        }

        if (group.type === "articles") {
          const soldKeys = group.articleKeys.filter((articleKey) => prices[articleKey] && getQuantity(articleKey));
          if (!soldKeys.length) return null;

          const groupTotal = soldKeys.reduce((total, key) => total + prices[key] * getQuantity(key), 0);

          return {
            title: t(`budget.groups.${group.key}`),
            columnLabels: hardwareLabels,
            total: format(groupTotal),
            items: soldKeys.map((articleKey) => ({
              name: t(`budget.articles.${articleKey}.name`),
              bullets: [t(`budget.articles.${articleKey}.description`)],
              imageSrc: itemImages[articleKey],
              price: format(prices[articleKey]),
              quantity: getQuantity(articleKey),
              total: format(prices[articleKey] * getQuantity(articleKey)),
            })),
          };
        }

        if (!prices[group.articleKey] || !therapists || !years) return null;

        return {
          title: t(`budget.groups.${group.key}`),
          columnLabels: licenseLabels,
          price: String(therapists),
          quantity: years,
          total: format(licenseTotal),
          items: [
            {
              name: `${t(`budget.articles.${group.articleKey}.name`)} ${t("pdf.licenseYears", { years })}`,
              bullets: t(`budget.articles.${group.articleKey}.features`, { returnObjects: true }),
            },
          ],
        };
      })
      .filter(Boolean);
  };

  const exportPdf = async (quoteDetails) => {
    setIsQuoteModalOpen(false);
    const language = i18n.language;

    try {
      // Carga diferida: el diagrama pesa mas de 2 MB y solo hace falta aqui, y
      // solo el del idioma en curso.
      const [{ default: generateQuotePdf }, headerImage, diagramImage] = await Promise.all([
        import("../../config/quotePdf"),
        import("../../assets/pdf/header.png"),
        language === "es"
          ? import("../../assets/pdf/es_RGS-EcosystemDiagram.png")
          : import("../../assets/pdf/en_RGS-EcosystemDiagram.png"),
      ]);

      await generateQuotePdf({
        t,
        quote: {
          ...quoteDetails,
          issueDate: formatDate(quoteDetails.issueDate, language),
          validUntil: formatDate(quoteDetails.validUntil, language),
        },
        distributor: distributorDetails,
        groups: buildPdfGroups(),
        totals: {
          subtotal: format(subtotal),
          discount,
          tax,
          final: format(getFinalTotal(subtotal, discount, tax)),
        },
        headerImageSrc: headerImage.default,
        diagramImageSrc: diagramImage.default,
      });
    } catch (error) {
      console.log("Generate quote PDF: ", error);
      toast.error(t("budget.pdfError"));
    }
  };

  const renderBudget = () => {
    const groups = budgetGroups.map(renderGroup).filter(Boolean);
    if (!groups.length) return <EmptyMessage>{t("budget.loadError")}</EmptyMessage>;

    return (
      <>
        {groups}
        <TotalsBox
          labels={t("budget.totals", { returnObjects: true })}
          subtotal={format(subtotal)}
          discount={discount}
          onDiscountChange={setDiscount}
          tax={tax}
          onTaxChange={setTax}
          finalTotal={format(getFinalTotal(subtotal, discount, tax))}
          onExportPdf={openQuoteModal}
        />
        {isQuoteModalOpen && <QuoteDetails onClose={() => setIsQuoteModalOpen(false)} onExport={exportPdf} />}
      </>
    );
  };

  return <DistributorLayout>{isLoading ? <Loader /> : renderBudget()}</DistributorLayout>;
};

export default BudgetGenerator;
